import {
  getAllowedVisitTypeCodesForState,
  getStateCode,
  STATE_VISIT_CONFIG,
  VisitTypeCode
} from '../data/state-visit-config';

export interface HopeNewPatientPrefill {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  birthSex?: string;
  email?: string;
  phoneNumber?: string;
  state?: string;
  treatmentType?: VisitTypeCode;
}

const HOPE_QUERY_KEYS = [
  'patient_type',
  'open_returning',
  'first_name',
  'last_name',
  'dob',
  'sex',
  'email',
  'phone',
  'state',
  'visit_type'
] as const;

function getSearchParams(search?: string): URLSearchParams {
  try {
    if (typeof search === 'string') {
      return new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    }
    return new URLSearchParams(typeof window !== 'undefined' ? window.location.search || '' : '');
  } catch {
    return new URLSearchParams('');
  }
}

function pickParam(params: URLSearchParams, keys: string[]): string {
  for (const key of keys) {
    const value = (params.get(key) || '').toString().trim();
    if (value) return value;
  }
  return '';
}

function mapSex(raw: string): string {
  const value = (raw || '').trim().toUpperCase();
  if (!value) return '';
  if (value === 'M' || value === 'MALE' || value.startsWith('M')) return 'male';
  if (value === 'F' || value === 'FEMALE' || value.startsWith('F')) return 'female';
  if (value === 'P' || value.includes('PREFER')) return 'prefer-not-to-say';
  return '';
}

function formatPhone(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function normalizeDob(raw: string): string {
  const value = (raw || '').trim().replace(/\./g, '/').replace(/-/g, '/');
  if (!value) return '';
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return '';
  const month = match[1].padStart(2, '0');
  const day = match[2].padStart(2, '0');
  const year = match[3];
  return `${month}/${day}/${year}`;
}

function normalizeVisitType(raw: string): VisitTypeCode | '' {
  const value = (raw || '').trim().toLowerCase();
  if (!value) return '';
  if (value === 'medication' || value.includes('medication')) return 'medication';
  if (value === 'psychotherapy' || value.includes('psych')) return 'psychotherapy';
  return '';
}

function isTruthyFlag(raw: string): boolean {
  const value = (raw || '').trim().toLowerCase();
  return value === '1' || value === 'true' || value === 'yes';
}

export function shouldOpenReturningPatientModal(search?: string): boolean {
  const params = getSearchParams(search);
  const patientType = pickParam(params, ['patient_type', 'patientType']).toLowerCase();
  const openReturning = pickParam(params, ['open_returning', 'openReturning']);
  return patientType === 'returning' && isTruthyFlag(openReturning);
}

export function hasHopeNewPatientHandoffParams(search?: string): boolean {
  const params = getSearchParams(search);
  const patientType = pickParam(params, ['patient_type', 'patientType']).toLowerCase();
  if (patientType === 'new') return true;

  const demographicKeys = [
    ['first_name', 'firstName'],
    ['last_name', 'lastName'],
    ['dob', 'birth_date', 'birthDate'],
    ['sex', 'birth_sex', 'birthSex', 'gender'],
    ['email', 'email_address', 'emailAddress'],
    ['phone', 'phone_number', 'phoneNumber'],
    ['state'],
    ['visit_type', 'visitType', 'treatment_type', 'treatmentType']
  ];
  return demographicKeys.some(keys => !!pickParam(params, keys));
}

export function parseHopeNewPatientPrefill(search?: string): HopeNewPatientPrefill | null {
  const params = getSearchParams(search);
  const prefill: HopeNewPatientPrefill = {};

  const firstName = pickParam(params, ['first_name', 'firstName']);
  if (firstName) prefill.firstName = firstName;

  const lastName = pickParam(params, ['last_name', 'lastName']);
  if (lastName) prefill.lastName = lastName;

  const birthDate = normalizeDob(pickParam(params, ['dob', 'birth_date', 'birthDate']));
  if (birthDate) prefill.birthDate = birthDate;

  const birthSex = mapSex(pickParam(params, ['sex', 'birth_sex', 'birthSex', 'gender']));
  if (birthSex) prefill.birthSex = birthSex;

  const email = pickParam(params, ['email', 'email_address', 'emailAddress']);
  if (email) prefill.email = email;

  const phoneNumber = formatPhone(pickParam(params, ['phone', 'phone_number', 'phoneNumber']));
  if (phoneNumber) prefill.phoneNumber = phoneNumber;

  const stateRaw = pickParam(params, ['state']);
  const stateCode = getStateCode(stateRaw);
  if (stateCode && STATE_VISIT_CONFIG.some(s => s.code === stateCode)) {
    prefill.state = stateCode;
  }

  const visitType = normalizeVisitType(pickParam(params, ['visit_type', 'visitType', 'treatment_type', 'treatmentType']));
  if (visitType) {
    if (prefill.state) {
      const allowed = getAllowedVisitTypeCodesForState(prefill.state);
      if (allowed.includes(visitType)) {
        prefill.treatmentType = visitType;
      }
    } else {
      prefill.treatmentType = visitType;
    }
  }

  return Object.keys(prefill).length > 0 ? prefill : null;
}

export function stripHopeHandoffParamsFromUrl(): void {
  try {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of HOPE_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {}
}
